import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import "flatpickr/dist/themes/material_blue.css";
import {
  FaPills,
  FaTags,
  FaBox,
  FaCalendarAlt,
  FaInfoCircle,
} from "react-icons/fa";
import axios from "axios";
import { baseurl } from "../../baseurl";
import Swal from "sweetalert2";
import { useParams, useNavigate } from "react-router-dom";

function EditMedicine() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [medicine, setMedicine] = useState({
    name: "",
    batchNumber: "",
    manufacturer: "",
    category: null,
    stock: "",
    unit: null,
    costPrice: "",
    sellingPrice: "",
    gst: null,
    expiryDate: new Date(),
    manufactureDate: new Date(),
    rackNumber: "",
    supplier: "",
    note: "",
    status: { value: "active", label: "Active" },
  });

  const [loading, setLoading] = useState(false);

  // Dropdown options
  const categoryOptions = [
    { value: "Tablet", label: "Tablet" },
    { value: "Capsule", label: "Capsule" },
    { value: "Syrup", label: "Syrup" },
    { value: "Injection", label: "Injection" },
    { value: "Ointment", label: "Ointment" },
  ];

  const unitOptions = [
    { value: "Capsule", label: "Capsule" },
    { value: "Box", label: "Box" },
    { value: "Strip", label: "Strip" },
    { value: "Bottle", label: "Bottle" },
    { value: "Pack", label: "Pack" },
    { value: "Tube", label: "Tube" },
  ];

  const gstOptions = [
    { value: 0, label: "0%" },
    { value: 5, label: "5%" },
    { value: 12, label: "12%" },
    { value: 18, label: "18%" },
    { value: 28, label: "28%" },
  ];

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "10px",
      borderColor: state.isFocused ? "#0d6efd" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(13,110,253,0.2)" : "none",
      "&:hover": { borderColor: "#0d6efd" },
      minHeight: "42px",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#0d6efd"
        : state.isFocused
        ? "#f0f7ff"
        : "white",
      color: state.isSelected ? "white" : "#212529",
    }),
  };

  // 🧠 Fetch Medicine Data by ID
  useEffect(() => {
    const fetchMedicine = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${baseurl}getMedicineById/${id}`);

        if (res.data.success) {
          const m = res.data.data;

          setMedicine({
            name: m.name,
            batchNumber: m.batch_number || "",
            manufacturer: m.manufacturer || "",
            category: categoryOptions.find((opt) => opt.value === m.category) || null,
            stock: m.stock || "",
            unit: unitOptions.find((opt) => opt.value === m.unit) || null,
            costPrice: m.cost_price || "",
            sellingPrice: m.selling_price || "",
            gst: gstOptions.find((opt) => opt.value === parseFloat(m.gst)) || null,
            expiryDate: m.expiry_date ? new Date(m.expiry_date) : new Date(),
            manufactureDate: m.manufacture_date
              ? new Date(m.manufacture_date)
              : new Date(),
            rackNumber: m.rack_number || "",
            supplier: m.supplier || "",
            note: m.note || "",
            status:
              statusOptions.find((opt) => opt.value === m.status) ||
              { value: "active", label: "Active" },
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Not Found",
            text: "Medicine data not found!",
          });
        }
      } catch (err) {
        console.error("❌ Error fetching medicine:", err);
        Swal.fire({
          icon: "error",
          title: "Failed to Load",
          text: "Could not load medicine details.",
        });
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchMedicine();
  }, [id]);

  // Handle input fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setMedicine({ ...medicine, [name]: value });
  };

  const handleDateChange = (name, date) => {
    setMedicine({ ...medicine, [name]: date[0] });
  };

  const handleSelectChange = (name, selectedOption) => {
    setMedicine({ ...medicine, [name]: selectedOption });
  };

  // 🧾 Update Medicine
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const payload = {
        name: medicine.name,
        batchNumber: medicine.batchNumber,
        manufacturer: medicine.manufacturer,
        category: medicine.category?.value || "",
        stock: medicine.stock,
        unit: medicine.unit?.value || "",
        costPrice: medicine.costPrice,
        sellingPrice: medicine.sellingPrice,
        gst: medicine.gst?.value || 0,
        expiryDate: medicine.expiryDate
          ? medicine.expiryDate.toISOString().split("T")[0]
          : null,
        manufactureDate: medicine.manufactureDate
          ? medicine.manufactureDate.toISOString().split("T")[0]
          : null,
        rackNumber: medicine.rackNumber,
        supplier: medicine.supplier,
        note: medicine.note,
        status: medicine.status?.value || "active",
      };

      console.log("🩺 Updating medicine:", payload);

      const response = await axios.put(`${baseurl}updateMedicine/${id}`, payload);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Updated",
          text: "Medicine updated successfully!",
          timer: 2000,
          showConfirmButton: false,
        });
        navigate("/all-medicine");
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.data.message || "Update failed.",
        });
      }
    } catch (error) {
      console.error("❌ Error updating medicine:", error);
      Swal.fire({
        icon: "error",
        title: "Request Failed",
        text: error.response?.data?.message || "Something went wrong!",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper bg-light">
        <div className="container py-4">
          <div className="row justify-content-center">
            <div className="col-lg-12">
              <div className="card border-0 shadow-lg rounded-4 animate__animated animate__fadeIn">
                <div
                  className="card-header bg-gradient text-white rounded-top-4"
                  style={{
                    background: "linear-gradient(90deg, #007bff, #00b4d8)",
                  }}
                >
                  <h4 className="mb-0 fw-bold d-flex align-items-center gap-2">
                    <FaPills /> Edit Medicine
                  </h4>
                </div>

                <div className="card-body p-4">
                  {loading ? (
                    <p className="text-center text-secondary py-5">
                      Loading medicine details...
                    </p>
                  ) : (
                    <form onSubmit={handleSubmit}>
                      {/* 🧾 Basic Info Section */}
                      <div className="section-header d-flex align-items-center gap-2 mb-3">
                        <FaInfoCircle className="text-primary" />
                        <h5 className="mb-0 fw-semibold text-secondary">
                          Basic Information
                        </h5>
                      </div>

                      <div className="row g-3 mb-4">
                        <div className="col-md-6">
                          <label className="form-label fw-semibold">
                            Medicine Name
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-lg"
                            name="name"
                            value={medicine.name}
                            onChange={handleChange}
                            placeholder="Enter medicine name"
                            required
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="form-label fw-semibold">
                            Batch Number
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-lg"
                            name="batchNumber"
                            value={medicine.batchNumber}
                            onChange={handleChange}
                            placeholder="e.g. BNX23"
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="form-label fw-semibold">
                            Pack Size
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-lg"
                            name="manufacturer"
                            value={medicine.manufacturer}
                            onChange={handleChange}
                            placeholder="Enter Pack Size"
                          />
                        </div>

                        <div className="col-md-4">
                          <label className="form-label fw-semibold">
                            Category
                          </label>
                          <Select
                            options={categoryOptions}
                            value={medicine.category}
                            onChange={(opt) => handleSelectChange("category", opt)}
                            styles={customSelectStyles}
                            placeholder="Select category"
                          />
                        </div>

                        <div className="col-md-2">
                          <label className="form-label fw-semibold">Stock</label>
                          <input
                            type="number"
                            className="form-control form-control-lg"
                            name="stock"
                            value={medicine.stock}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-2">
                          <label className="form-label fw-semibold">Unit</label>
                          <Select
                            options={unitOptions}
                            value={medicine.unit}
                            onChange={(opt) => handleSelectChange("unit", opt)}
                            styles={customSelectStyles}
                            placeholder="Select unit"
                          />
                        </div>

                        <div className="col-md-2">
                          <label className="form-label fw-semibold">
                            Cost Price (₹)
                          </label>
                          <input
                            type="number"
                            className="form-control form-control-lg"
                            name="costPrice"
                            value={medicine.costPrice}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-2">
                          <label className="form-label fw-semibold">
                            Selling Price (₹)
                          </label>
                          <input
                            type="number"
                            className="form-control form-control-lg"
                            name="sellingPrice"
                            value={medicine.sellingPrice}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-2">
                          <label className="form-label fw-semibold">GST (%)</label>
                          <Select
                            options={gstOptions}
                            value={medicine.gst}
                            onChange={(opt) => handleSelectChange("gst", opt)}
                            styles={customSelectStyles}
                            placeholder="Select GST"
                          />
                        </div>
                      </div>

                      {/* 🗓️ Date & Supplier Section */}
                      <div className="section-header d-flex align-items-center gap-2 mb-3">
                        <FaCalendarAlt className="text-primary" />
                        <h5 className="mb-0 fw-semibold text-secondary">
                          Dates & Supplier
                        </h5>
                      </div>

                      <div className="row g-3 mb-4">
                        <div className="col-md-3">
                          <label className="form-label fw-semibold">
                            Manufacture Date
                          </label>
                          <Flatpickr
                            value={medicine.manufactureDate}
                            onChange={(date) =>
                              handleDateChange("manufactureDate", date)
                            }
                            options={{ dateFormat: "Y-m-d" }}
                            className="form-control form-control-lg"
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="form-label fw-semibold">
                            Expiry Date
                          </label>
                          <Flatpickr
                            value={medicine.expiryDate}
                            onChange={(date) =>
                              handleDateChange("expiryDate", date)
                            }
                            options={{ dateFormat: "Y-m-d" }}
                            className="form-control form-control-lg"
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="form-label fw-semibold">
                            Rack Number
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-lg"
                            name="rackNumber"
                            value={medicine.rackNumber}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="form-label fw-semibold">Supplier</label>
                          <input
                            type="text"
                            className="form-control form-control-lg"
                            name="supplier"
                            value={medicine.supplier}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      {/* 🧾 Notes & Status Section */}
                      <div className="section-header d-flex align-items-center gap-2 mb-3">
                        <FaTags className="text-primary" />
                        <h5 className="mb-0 fw-semibold text-secondary">
                          Notes & Status
                        </h5>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-9">
                          <label className="form-label fw-semibold">
                            Additional Notes
                          </label>
                          <textarea
                            className="form-control"
                            rows="3"
                            name="note"
                            value={medicine.note}
                            onChange={handleChange}
                          ></textarea>
                        </div>

                        <div className="col-md-3">
                          <label className="form-label fw-semibold">Status</label>
                          <Select
                            options={statusOptions}
                            value={medicine.status}
                            onChange={(opt) =>
                              handleSelectChange("status", opt)
                            }
                            styles={customSelectStyles}
                            placeholder="Select status"
                          />
                        </div>
                      </div>

                      <div className="text-end mt-4">
                        <button
                          type="submit"
                          disabled={loading}
                          className="btn btn-primary btn-lg px-5 py-2 rounded-3 shadow-sm"
                        >
                          💾 Update Medicine
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default EditMedicine;
